// Фигура с сегментами: силуэт (руки, корпус, ноги) и список «кг · % нормы · стрелка к предыдущему
// замеру». Цвет сегмента — по % нормы, как в DDX: ниже нормы, норма, выше нормы.

import { MuscleMap } from '@/components/muscle-map/MuscleMap'
import type { Sex } from '@/components/muscle-map/muscles'
import { cn } from '@/lib/cn'
import { formatNumber, formatSigned } from '@/lib/format'

import type { Segment, SegmentValue, Segments } from './api'
import { SEGMENT_LABELS } from './labels'
import { segmentColor, type FigureMode } from './segmentColor'

const ARM = new Set([
  'anterior_deltoid', 'lateral_deltoid', 'posterior_deltoid', 'biceps_brachii', 'brachialis',
  'triceps_brachii', 'forearm_flexors', 'forearm_extensors',
])
const LEG = new Set([
  'tensor_fasciae_latae', 'sartorius', 'rectus_femoris', 'vastus_lateralis', 'vastus_medialis',
  'hip_adductors', 'patella_region', 'patellar_tendon_region', 'tibialis_anterior', 'fibularis_group',
  'gastrocnemius', 'soleus', 'iliotibial_tract', 'biceps_femoris', 'medial_hamstrings',
  'popliteal_region', 'achilles_tendon',
])

function segmentAt(id: string, x: number): Segment | undefined {
  if (!Number.isFinite(x) || x >= 625 || id === 'sternocleidomastoid') return
  // На виде спереди левая сторона человека справа на рисунке.
  const left = x > 310
  if (ARM.has(id)) return left ? 'left_arm' : 'right_arm'
  if (LEG.has(id)) return left ? 'left_leg' : 'right_leg'
  return 'trunk'
}

const ORDER: readonly Segment[] = ['left_arm', 'right_arm', 'trunk', 'left_leg', 'right_leg']

function change(current: SegmentValue, before: SegmentValue | undefined, mode: FigureMode) {
  if (!before) return null
  const delta = Math.round((current.kg - before.kg) * 100) / 100
  if (delta === 0) return { delta, arrow: '→', good: null as boolean | null }
  const up = delta > 0
  return { delta, arrow: up ? '▲' : '▼', good: mode === 'lean' ? up : !up }
}

type BodyFigureProps = {
  segments: Segments
  previous: Segments | null
  sex: Sex
}

function SegmentMap({ segments, mode, sex, label }: Omit<BodyFigureProps, 'previous'> & {
  mode: FigureMode
  label: string
}) {
  return (
    <MuscleMap
      sex={sex}
      label={label}
      className="w-full max-w-36"
      frontOnly
      muscleFill={(id, x) => {
        const segment = segmentAt(id, x)
        return segment ? segmentColor(segments[mode][segment]?.pct_of_norm, mode) : undefined
      }}
    />
  )
}

function SegmentDot({ value, mode }: { value?: SegmentValue; mode: FigureMode }) {
  return (
    <span
      className="size-3 shrink-0 rounded-full"
      style={{ background: segmentColor(value?.pct_of_norm, mode) }}
      aria-hidden
    />
  )
}

function SegmentNumbers({ value, before, mode }: { value?: SegmentValue; before?: SegmentValue; mode: FigureMode }) {
  if (!value) return <span className="text-muted">—</span>
  const diff = change(value, before, mode)
  return (
    <>
      <span className="whitespace-nowrap tabular-nums text-fg">{formatNumber(value.kg, { digits: 2 })} кг</span>
      {value.pct_of_norm != null ? (
        <span className="whitespace-nowrap tabular-nums text-muted">{formatNumber(value.pct_of_norm, { digits: 1 })} %</span>
      ) : null}
      {diff ? (
        <span
          className={cn(
            'whitespace-nowrap tabular-nums',
            diff.good === null ? 'text-muted' : diff.good ? 'text-accent' : 'text-danger',
          )}
        >
          {diff.arrow} {formatSigned(diff.delta, { digits: 2 })}
        </span>
      ) : null}
    </>
  )
}

export function BodyFigure({ segments, previous, sex }: BodyFigureProps) {
  return (
    <div className="grid grid-cols-2 items-center gap-x-3 gap-y-2 @min-[48rem]:grid-cols-[7rem_minmax(0,1fr)_7rem] @min-[48rem]:gap-x-4 @min-[56rem]:grid-cols-[9rem_minmax(0,1fr)_9rem]">
      <div className="grid justify-items-center @min-[48rem]:block">
        <p className="mb-1 text-sm font-medium text-muted @min-[48rem]:hidden">Мышцы</p>
        <SegmentMap segments={segments} mode="lean" sex={sex} label="Карта мышц, вид спереди" />
      </div>
      <table className="col-span-2 row-start-2 w-full table-fixed border-separate border-spacing-y-1.5 text-center text-xs @min-[48rem]:col-span-1 @min-[48rem]:row-start-auto @min-[48rem]:text-sm" aria-label="Мышцы и жир по сегментам">
        <thead className="sr-only @min-[48rem]:not-sr-only">
          <tr className="text-muted">
            <th scope="col" className="w-[37%] pb-1 font-medium">Мышцы</th>
            <th scope="col" className="pb-1 font-medium"><span className="sr-only">Сегмент</span></th>
            <th scope="col" className="w-[37%] pb-1 font-medium">Жир</th>
          </tr>
        </thead>
        <tbody>
          {ORDER.map((segment) => (
            <tr key={segment} data-testid={`segment-${segment}`} className="bg-surface-2">
              <td className="rounded-l-lg px-1 py-2 @min-[48rem]:px-2">
                <div className="flex flex-wrap items-center justify-center gap-x-1 gap-y-0.5 @min-[48rem]:gap-x-2">
                  <SegmentDot value={segments.lean[segment]} mode="lean" />
                  <SegmentNumbers value={segments.lean[segment]} before={previous?.lean[segment]} mode="lean" />
                </div>
              </td>
              <th scope="row" className="px-1 font-medium text-fg">{SEGMENT_LABELS[segment]}</th>
              <td className="rounded-r-lg px-1 py-2 @min-[48rem]:px-2">
                <div className="flex flex-wrap items-center justify-center gap-x-1 gap-y-0.5 @min-[48rem]:gap-x-2">
                  <SegmentDot value={segments.fat[segment]} mode="fat" />
                  <SegmentNumbers value={segments.fat[segment]} before={previous?.fat[segment]} mode="fat" />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="grid justify-items-center @min-[48rem]:block">
        <p className="mb-1 text-sm font-medium text-muted @min-[48rem]:hidden">Жир</p>
        <SegmentMap segments={segments} mode="fat" sex={sex} label="Карта жира, вид спереди" />
      </div>
    </div>
  )
}
